import axios from 'axios';
import { getUrl } from '../service-utils';
import authHeader from '../auth-header';

export interface ITripPhoto {
  id: string;
  tripId: string;
  s3Path: string;
  photo_url?: string;
  fileSize: number;
  fileSizeMB?: number;
  mimeType: string;
  width?: number;
  height?: number;
  orientation?: 'landscape' | 'portrait' | 'square';
  resolution?: string;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  hasLocation?: boolean;
  caption?: string;
  hasCaption?: boolean;
  takenAt?: string | Date;
  uploadedBy?: string;
  uploadedAt?: string | Date;
  cameraMake?: string;
  cameraModel?: string;
  hasCameraData?: boolean;
  isActive?: boolean;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export interface IPhotoAnalytics {
  totalPhotos: number;
  activePhotos: number;
  photosWithLocation: number;
  photosWithCaption: number;
  photosWithCameraData: number;
  photosByUploader: Record<string, number>;
  photosByMimeType: Record<string, number>;
  totalSize: number;
  averageFileSize: number;
  averageResolution: { width: number; height: number };
  landscapePhotos: number;
  portraitPhotos: number;
  squarePhotos: number;
  oldestPhoto?: Date;
  newestPhoto?: Date;
}

export interface IUploadPhotoResponse {
  status: string;
  data: {
    photo: ITripPhoto;
  };
}

export interface IUploadPhotosResponse {
  status: string;
  data: {
    photos: ITripPhoto[];
    count: number;
  };
}

export interface IGetPhotosResponse {
  status: string;
  results: number;
  data: {
    photos: ITripPhoto[];
  };
}

export interface IPhotoAnalyticsResponse {
  status: string;
  data: {
    analytics: IPhotoAnalytics;
  };
}

export interface IPhotoUrlResponse {
  status: string;
  data: {
    url: string;
    expiresIn: number;
  };
}

class TripPhotoService {
  private API_URL: string;

  constructor() {
    this.API_URL = getUrl();
  }

  /**
   * Upload a single photo to a trip
   */
  public async uploadPhoto(
    tripId: string,
    file: File,
    caption?: string,
    placeId?: string
  ): Promise<ITripPhoto> {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      if (caption) {
        formData.append('caption', caption);
      }
      if (placeId) {
        formData.append('placeId', placeId);
      }

      const response = await axios.post<IUploadPhotoResponse>(
        `${this.API_URL}trips/${tripId}/photos`,
        formData,
        {
          headers: {
            ...authHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return response.data.data.photo;
    } catch (error: any) {
      console.error(
        'Error uploading photo:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Upload multiple photos to a trip
   */
  public async uploadPhotos(
    tripId: string,
    files: File[]
  ): Promise<ITripPhoto[]> {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('photos', file);
      });

      const response = await axios.post<IUploadPhotosResponse>(
        `${this.API_URL}trips/${tripId}/photos/batch`,
        formData,
        {
          headers: {
            ...authHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return response.data.data.photos;
    } catch (error: any) {
      console.error(
        'Error uploading photos:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get all photos for a trip
   */
  public async getTripPhotos(tripId: string): Promise<ITripPhoto[]> {
    try {
      const response = await axios.get<IGetPhotosResponse>(
        `${this.API_URL}trips/${tripId}/photos`,
        { headers: authHeader() }
      );
      return response.data.data.photos;
    } catch (error: any) {
      console.error(
        'Error fetching trip photos:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get a specific photo
   */
  public async getPhoto(tripId: string, photoId: string): Promise<ITripPhoto> {
    try {
      const response = await axios.get<IUploadPhotoResponse>(
        `${this.API_URL}trips/${tripId}/photos/${photoId}`,
        { headers: authHeader() }
      );
      return response.data.data.photo;
    } catch (error: any) {
      console.error(
        'Error fetching photo:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get photos with location data
   */
  public async getPhotosWithLocation(tripId: string): Promise<ITripPhoto[]> {
    try {
      const response = await axios.get<IGetPhotosResponse>(
        `${this.API_URL}trips/${tripId}/photos/with-location`,
        { headers: authHeader() }
      );
      return response.data.data.photos;
    } catch (error: any) {
      console.error(
        'Error fetching photos with location:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get photos by uploader
   */
  public async getPhotosByUploader(
    tripId: string,
    uploaderId: string
  ): Promise<ITripPhoto[]> {
    try {
      const response = await axios.get<IGetPhotosResponse>(
        `${this.API_URL}trips/${tripId}/photos/uploader/${uploaderId}`,
        { headers: authHeader() }
      );
      return response.data.data.photos;
    } catch (error: any) {
      console.error(
        'Error fetching photos by uploader:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Update photo caption
   */
  public async updatePhotoCaption(
    tripId: string,
    photoId: string,
    caption: string
  ): Promise<ITripPhoto> {
    try {
      const response = await axios.patch<IUploadPhotoResponse>(
        `${this.API_URL}trips/${tripId}/photos/${photoId}/caption`,
        { caption },
        { headers: authHeader() }
      );
      return response.data.data.photo;
    } catch (error: any) {
      console.error(
        'Error updating photo caption:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Update photo location
   */
  public async updatePhotoLocation(
    tripId: string,
    photoId: string,
    latitude: number,
    longitude: number,
    altitude?: number
  ): Promise<ITripPhoto> {
    try {
      const response = await axios.patch<IUploadPhotoResponse>(
        `${this.API_URL}trips/${tripId}/photos/${photoId}/location`,
        { latitude, longitude, altitude },
        { headers: authHeader() }
      );
      return response.data.data.photo;
    } catch (error: any) {
      console.error(
        'Error updating photo location:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Remove photo location
   */
  public async removePhotoLocation(
    tripId: string,
    photoId: string
  ): Promise<ITripPhoto> {
    try {
      const response = await axios.delete<IUploadPhotoResponse>(
        `${this.API_URL}trips/${tripId}/photos/${photoId}/location`,
        { headers: authHeader() }
      );
      return response.data.data.photo;
    } catch (error: any) {
      console.error(
        'Error removing photo location:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Delete a photo (soft delete)
   */
  public async deletePhoto(tripId: string, photoId: string): Promise<void> {
    try {
      await axios.delete(`${this.API_URL}trips/${tripId}/photos/${photoId}`, {
        headers: authHeader()
      });
    } catch (error: any) {
      console.error(
        'Error deleting photo:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Permanently delete a photo (hard delete with S3 removal)
   */
  public async permanentlyDeletePhoto(
    tripId: string,
    photoId: string
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.API_URL}trips/${tripId}/photos/${photoId}/permanent`,
        { headers: authHeader() }
      );
    } catch (error: any) {
      console.error(
        'Error permanently deleting photo:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get photo analytics
   */
  public async getPhotoAnalytics(tripId: string): Promise<IPhotoAnalytics> {
    try {
      const response = await axios.get<IPhotoAnalyticsResponse>(
        `${this.API_URL}trips/${tripId}/photos/analytics`,
        { headers: authHeader() }
      );
      return response.data.data.analytics;
    } catch (error: any) {
      console.error(
        'Error fetching photo analytics:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get signed URL for photo
   */
  public async getPhotoUrl(
    tripId: string,
    photoId: string,
    expiresIn: number = 3600
  ): Promise<string> {
    try {
      const response = await axios.get<IPhotoUrlResponse>(
        `${this.API_URL}trips/${tripId}/photos/${photoId}/url?expiresIn=${expiresIn}`,
        { headers: authHeader() }
      );
      return response.data.data.url;
    } catch (error: any) {
      console.error(
        'Error fetching photo URL:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Like or dislike a photo
   */
  public async likePhoto(
    tripId: string,
    photoId: string,
    isLike: boolean = true
  ): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}trips/${tripId}/photos/${photoId}/like`,
        { isLike },
        { headers: authHeader() }
      );
    } catch (error: any) {
      console.error(
        'Error liking photo:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Unlike a photo
   */
  public async unlikePhoto(tripId: string, photoId: string): Promise<void> {
    try {
      await axios.delete(
        `${this.API_URL}trips/${tripId}/photos/${photoId}/like`,
        { headers: authHeader() }
      );
    } catch (error: any) {
      console.error(
        'Error unliking photo:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get current user's like status for a photo
   */
  public async getMyPhotoLike(
    tripId: string,
    photoId: string
  ): Promise<{ hasLiked: boolean; hasDisliked: boolean } | null> {
    try {
      const response = await axios.get(
        `${this.API_URL}trips/${tripId}/photos/${photoId}/like/me`,
        { headers: authHeader() }
      );
      return response.data.data.like;
    } catch (error: any) {
      console.error(
        'Error getting like status:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get photo engagement stats
   */
  public async getPhotoEngagement(
    tripId: string,
    photoId: string
  ): Promise<{
    likesCount: number;
    dislikesCount: number;
    commentsCount: number;
    netLikes: number;
  }> {
    try {
      const response = await axios.get(
        `${this.API_URL}trips/${tripId}/photos/${photoId}/engagement`,
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error getting engagement:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }
}

const tripPhotoServiceInstance = new TripPhotoService();
export default tripPhotoServiceInstance;
